import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Badge, Chip } from '@mui/material';
// Conflict analysis now comes from the backend API
interface OrderConflictAnalysis {
  orderId: number;
  hasConflicts: boolean;
  conflictCount: number;
  highestSeverity: 'none' | 'warning' | 'error' | 'critical';
  conflicts: any[];
  suggestedResolutions: string[];
}
import {
  formatDecimalHours,
  formatHour,
  timeStringToDecimalHours,
  extractTimeString,
} from '../utils/TimeUtils';
import { calculateBreakTimeOverlap } from '../utils/BreakTimeUtils';

interface GanttTask {
  id: string;
  name: string;
  start: string;
  end: string;
  progress: number;
  custom_class?: string;
  estimatedHours?: number;
  conflictAnalysis?: OrderConflictAnalysis;
  production_line_id?: number;
  production_line_name?: string;
  // Time properties for proper positioning
  startTime?: string;
  endTime?: string;
  // Unified spanning properties
  is_split_order?: boolean;
  spanning_type?: 'break' | 'day_end';
  part?: 'p1' | 'p2' | 'p3' | 'p4' | 'p5' | 'p6' | 'p7' | 'p8' | 'p9' | 'p10' | 'p11' | 'p12'; // Indicates which part of a split order this task represents (p1=first part, p2=second part, etc.)
  first_portion_minutes?: number;
  second_portion_minutes?: number;
  second_portion_date?: string;
  second_portion_start_time?: string;
  // Legacy break-spanning properties (for migration)
  pre_break_minutes?: number;
  post_break_minutes?: number;
  break_pause_start?: string;
  break_pause_end?: string;
}

interface ProductionLineWithCapacity {
  id: number;
  name: string;
  description?: string;
  defaultWorkers: number;
  lineType?: 'manual' | 'machine';
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  current_utilization?: number;
  total_orders?: number;
  capacity_percentage?: number;
}

interface MultiLineGanttProps {
  tasks: GanttTask[];
  viewMode?: 'day' | 'multi';
  currentDate?: Date;
  rollingDays?: number;
  actualViewMode?: 'Day' | 'Week' | 'Rolling';
  onTaskClick?: (task: GanttTask) => void;
  onTaskDateChange?: (taskId: string, start: Date, end: Date) => void;
  onTaskProgressChange?: (taskId: string, progress: number) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  unscheduledTasks?: GanttTask[];
  pastDueTasks?: GanttTask[];
  productionOrders?: any[];
  availableHours?: Map<string, number>;
  workersCache?: Map<string, number>;
  workingHoursConfig?: Map<
    string,
    {
      startTime: string;
      endTime: string;
      breakStartTime: string;
      breakEndTime: string;
      workers: number;
    }
  >;
  isActiveCache?: Map<string, boolean>;
  productionLines?: ProductionLineWithCapacity[];
  selectedProductionLineId?: number | null;
  onLineFilterChange?: (lineId: number | null) => void;
}

const MultiLineGantt: React.FC<MultiLineGanttProps> = ({
  tasks,
  viewMode = 'multi',
  currentDate = new Date(),
  rollingDays = 30,
  actualViewMode = 'Rolling',
  onTaskDateChange,
  unscheduledTasks = [],
  pastDueTasks = [],
  productionOrders = [],
  availableHours = new Map(),
  workersCache: _workersCache = new Map(),
  workingHoursConfig = new Map(),
  isActiveCache = new Map(),
  productionLines = [],
  selectedProductionLineId = null,
  onLineFilterChange: _onLineFilterChange,
}) => {
  // State management - MUST be called unconditionally (Rules of Hooks)
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [editDate, setEditDate] = useState<string>('');
  const [editTime, setEditTime] = useState<string>('');
  const [sidebarTab, setSidebarTab] = useState(0);
  const [_showAllLines, _setShowAllLines] = useState(true);
  const [_collapsedLines, _setCollapsedLines] = useState<Set<number>>(new Set());

  // Constants
  const HOUR_HEIGHT = 80;
  const TIME_COLUMN_WIDTH = 80;
  const _LINE_HEADER_HEIGHT = 60;
  const SIDEBAR_WIDTH = 320;

  // Helper functions
  const getTaskColor = (customClass?: string) => {
    switch (customClass) {
      case 'gantt-task-completed':
        return '#4caf50';
      case 'gantt-task-in-progress':
        return '#2196f3';
      case 'gantt-task-urgent':
        return '#f44336';
      case 'gantt-task-high':
        return '#ff5722';
      default:
        return '#ff9800';
    }
  };

  // Production line colors for visual distinction
  const getLineColor = (lineId: number) => {
    const colors = [
      '#1976d2', // Blue
      '#388e3c', // Green
      '#f57c00', // Orange
      '#7b1fa2', // Purple
      '#d32f2f', // Red
      '#0288d1', // Light Blue
      '#689f38', // Light Green
      '#f9a825', // Amber
    ];
    return colors[lineId % colors.length];
  };

  // Get working hours configuration for a specific date
  const getWorkingHoursForDate = (date: Date) => {
    const dateKey = date.toISOString().split('T')[0];
    const config = workingHoursConfig?.get(dateKey);

    // Return default working hours if config is missing
    if (!config) {
      return {
        startTime: '08:00:00',
        endTime: '17:00:00',
        breakStartTime: '',
        breakEndTime: '',
        workers: 12,
      };
    }

    return config;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Data processing - group tasks by production line
  const visibleTasks = tasks.filter((task) => task.custom_class !== 'gantt-anchor-hidden');

  // Group tasks by production line
  const tasksByLine = (() => {
    const grouped = new Map<number, GanttTask[]>();

    // Initialize with all active production lines
    productionLines.forEach((line) => {
      if (line.isActive) {
        grouped.set(line.id, []);
      }
    });

    // Group tasks by their production line
    // Use task's production_line_id (which should already have product fallback from ProductionSchedule)
    visibleTasks.forEach((task) => {
      const lineId = task.production_line_id || 1; // Default to line 1 if not specified
      if (!grouped.has(lineId)) {
        grouped.set(lineId, []);
      }
      grouped.get(lineId)!.push(task);
    });

    return grouped;
  })();

  // Filter lines based on selection
  const visibleLines = (() => {
    if (selectedProductionLineId !== null) {
      return productionLines.filter((line) => line.id === selectedProductionLineId);
    }
    return productionLines.filter((line) => line.isActive);
  })();

  // Date range calculation - include all dates, even weekends/non-working days
  // This ensures orders scheduled on weekends are visible
  const dateRange = (() => {
    if (viewMode === 'day') {
      return [new Date(currentDate)];
    }

    const dates: Date[] = [];
    if (actualViewMode === 'Rolling') {
      for (let i = 0; i < rollingDays; i++) {
        const date = new Date(currentDate);
        date.setDate(currentDate.getDate() + i);
        dates.push(date);
      }
    } else {
      const startOfWeek = new Date(currentDate);
      const dayOfWeek = startOfWeek.getDay();
      const sundayOffset = -dayOfWeek;
      startOfWeek.setDate(startOfWeek.getDate() + sundayOffset);

      for (let i = 0; i <= 6; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
    }

    // Don't filter out dates - show all dates including weekends
    // This ensures orders scheduled on weekends are visible in Rolling view
    return dates;
  })();

  // Tasks by date for each line
  const tasksByDateAndLine = (() => {
    const grouped = new Map<number, Map<string, GanttTask[]>>();

    visibleLines.forEach((line) => {
      const lineTasksByDate = new Map<string, GanttTask[]>();
      const lineTasks = tasksByLine.get(line.id) || [];

      lineTasks.forEach((task) => {
        const dateKey = task.start;
        if (!lineTasksByDate.has(dateKey)) {
          lineTasksByDate.set(dateKey, []);
        }
        lineTasksByDate.get(dateKey)!.push(task);
      });

      // Sort tasks within each date by start time
      lineTasksByDate.forEach((tasks, _dateKey) => {
        tasks.sort((a, b) => {
          // Get start time for comparison
          const timeA = a.startTime ? timeStringToDecimalHours(a.startTime) : 0;
          const timeB = b.startTime ? timeStringToDecimalHours(b.startTime) : 0;
          return timeA - timeB;
        });
      });

      grouped.set(line.id, lineTasksByDate);
    });

    return grouped;
  })();

  // Get the overall time range across all dates - handle errors safely
  // This considers both configured working hours AND actual task times
  const overallTimeRange = (() => {
    // Check if date range is empty - don't throw, return null to handle gracefully
    if (dateRange.length === 0) {
      return null;
    }

    let earliestStart = 24;
    let latestEnd = 0;

    // First, consider configured working hours
    dateRange.forEach((date) => {
      const workingHours = getWorkingHoursForDate(date);
      if (workingHours) {
        const startHour = timeStringToDecimalHours(extractTimeString(workingHours.startTime));
        const endHour = timeStringToDecimalHours(extractTimeString(workingHours.endTime));
        earliestStart = Math.min(earliestStart, Math.floor(startHour));
        latestEnd = Math.max(latestEnd, Math.ceil(endHour));
      }
    });

    // Also consider actual task start/end times to ensure all tasks are visible
    visibleTasks.forEach((task) => {
      if (task.startTime) {
        const taskStartHour = timeStringToDecimalHours(extractTimeString(task.startTime));
        earliestStart = Math.min(earliestStart, Math.floor(taskStartHour));
      }
      if (task.endTime) {
        const taskEndHour = timeStringToDecimalHours(extractTimeString(task.endTime));
        latestEnd = Math.max(latestEnd, Math.ceil(taskEndHour));
      }
    });

    // Fallback if no working hours found (should not happen now with default fallback)
    if (earliestStart === 24) {
      return null;
    }

    const totalHours = latestEnd - earliestStart;
    const result = {
      startHour: earliestStart,
      endHour: latestEnd,
      totalHours,
      hours: Array.from({ length: totalHours }, (_, i) => earliestStart + i),
    };

    return result;
  })();

  // Handle empty date range or invalid time range gracefully - store error for display
  const hasError = !overallTimeRange;
  const errorMessage = hasError
    ? `No dates with available hours in the ${dateRange.length > 0 ? dateRange.map((d) => d.toISOString().split('T')[0]).join(', ') : 'selected range'}. Please check your working hours configuration.`
    : null;

  // Use default hours if overallTimeRange is null (all hooks must run)
  const hours = overallTimeRange?.hours || [];
  const shouldScroll = dateRange.length > 7;
  const shouldShowSidebar = unscheduledTasks.length > 0 || pastDueTasks.length > 0;
  const gap = 16; // gap: 2 in Material-UI is 16px
  // Account for: page padding (48px = 24px * 2), time column, and unscheduled/past due sidebar
  const availableWidth =
    window.innerWidth - (shouldShowSidebar ? SIDEBAR_WIDTH + gap : 0) - TIME_COLUMN_WIDTH - 48;

  const dayWidth = (() => {
    if (actualViewMode === 'Day') {
      // In day mode, let lines expand to fill available space
      // Use auto or a very large value to let flex handle it
      return 'auto';
    } else {
      const calculatedWidth = Math.max(140, Math.min(180, availableWidth / dateRange.length));
      return calculatedWidth;
    }
  })();

  // Event handlers
  const handleTaskClick = useCallback(
    (task: GanttTask) => {
      // Use composite key for split tasks: id-part, or just id for regular tasks
      const taskKey = task.part ? `${task.id}-${task.part}` : task.id;
      if (editingTask === taskKey) {
        setEditingTask(null);
      } else {
        setEditingTask(taskKey);

        const originalOrder = productionOrders?.find(
          (order) => String(order.production_order_id) === task.id
        );

        if (originalOrder) {
          if (originalOrder.planned_production_date) {
            // Use UTC date to avoid timezone issues
            const date = new Date(originalOrder.planned_production_date);
            setEditDate(date.toISOString().split('T')[0]);
          } else {
            setEditDate(new Date().toISOString().split('T')[0]);
          }

          // For split orders, use the correct time based on the part
          let timeToUse: string | undefined = undefined;
          if (task.part && originalOrder.is_split_order) {
            // For split orders, get time from schedule_portions array
            const schedulePortions = (originalOrder as any).schedule_portions as
              | Array<{
                  portion_number: number;
                  scheduled_date: string;
                  scheduled_time: string;
                  duration_minutes: number;
                }>
              | undefined;

            if (schedulePortions) {
              // Extract part number from task.part (e.g., 'p2' -> 2)
              const partNumber = parseInt(task.part.replace('p', '')) - 1;
              const portion = schedulePortions[partNumber];
              if (portion) {
                timeToUse = portion.scheduled_time;
              }
            }

            // Fallback to legacy fields for backward compatibility
            if (!timeToUse && task.part === 'p2') {
              timeToUse = originalOrder.second_portion_start_time || originalOrder.break_pause_end;
            }
          }

          if (!timeToUse && originalOrder.planned_production_time) {
            // For regular orders or fallback, use planned_production_time
            timeToUse = originalOrder.planned_production_time;
          }

          if (timeToUse) {
            // Handle datetime strings like "1970-01-01T08:30:00.000Z"
            if (timeToUse.includes('T')) {
              const date = new Date(timeToUse);
              const hours = date.getUTCHours().toString().padStart(2, '0');
              const minutes = date.getUTCMinutes().toString().padStart(2, '0');
              setEditTime(`${hours}:${minutes}`);
            } else {
              // Handle simple time strings like "08:30:00" or "13:00:00"
              const timeParts = timeToUse.split(':');
              setEditTime(`${timeParts[0]}:${timeParts[1]}`);
            }
          } else {
            setEditTime('08:00');
          }
        } else {
          const today = new Date();
          setEditDate(today.toISOString().split('T')[0]);
          // Set default time to the start of working hours for today
          const workingHours = getWorkingHoursForDate(today);
          if (workingHours) {
            const startTime = workingHours.startTime.substring(0, 5); // Get HH:MM format
            setEditTime(startTime);
          } else {
            setEditTime('08:00');
          }
        }
      }
    },
    [editingTask, productionOrders]
  );

  const handleEditOrder = useCallback((taskId: string) => {
    window.open(`/production-orders?orderId=${taskId}`, '_blank');
    setEditingTask(null);
  }, []);

  const handleCloseMiniPopup = useCallback(() => {
    setEditingTask(null);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingTask && onTaskDateChange && editDate && editTime) {
      const [hours, minutes] = editTime.split(':').map(Number);
      const dateParts = editDate.split('-').map(Number);
      const newDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2], hours, minutes, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const compareDate = new Date(newDate);
      compareDate.setHours(0, 0, 0, 0);

      if (compareDate < today) {
        alert(
          'Cannot schedule production orders for past dates. Please select today or a future date.'
        );
        return;
      }

      onTaskDateChange(editingTask, newDate, newDate);
      setEditingTask(null);
    }
  }, [editingTask, onTaskDateChange, editDate, editTime]);

  const handleUnschedule = useCallback(
    (taskId: string) => {
      if (onTaskDateChange) {
        const invalidDate = new Date(NaN);
        onTaskDateChange(taskId, invalidDate, invalidDate);
      }
      setEditingTask(null);
    },
    [onTaskDateChange]
  );

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingTask) {
        const target = event.target as Element;
        if (!target.closest('[data-mini-popup]')) {
          setEditingTask(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingTask]);

  // Line capacity calculation
  const calculateLineCapacity = (lineId: number, date: Date) => {
    const dateKey = date.toISOString().split('T')[0];
    const lineTasksByDate = tasksByDateAndLine.get(lineId);
    const dayTasks = lineTasksByDate?.get(dateKey) || [];

    const totalHours = dayTasks.reduce((sum, task) => {
      const hours =
        typeof task.estimatedHours === 'number'
          ? task.estimatedHours
          : typeof task.estimatedHours === 'string'
            ? parseFloat(task.estimatedHours)
            : NaN;

      if (!isNaN(hours) && hours > 0) {
        return sum + hours;
      } else {
        throw new Error(
          `Invalid task hours for task: ${task.name}, estimatedHours: ${task.estimatedHours} (type: ${typeof task.estimatedHours})`
        );
      }
    }, 0);

    const dayAvailableHours = availableHours.get(dateKey) || 0;
    const utilization = dayAvailableHours > 0 ? (totalHours / dayAvailableHours) * 100 : 0;

    return {
      totalHours,
      availableHours: dayAvailableHours,
      utilization,
      taskCount: dayTasks.length,
    };
  };

  // Task chip component
  const TaskChip = React.memo<{ task: GanttTask; showDueDate?: boolean }>(
    ({ task, showDueDate = false }) => {
      // Task ID is now the original order ID
      const originalOrder = productionOrders?.find(
        (order) => String(order.production_order_id) === task.id
      );

      return (
        <Box
          sx={{
            bgcolor: getTaskColor(task.custom_class),
            color: 'white',
            cursor: 'pointer',
            mb: 1,
            mr: 1,
            borderRadius: 2,
            p: 1,
            minHeight: editingTask === task.id ? 'auto' : showDueDate ? 56 : 32,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxShadow: editingTask === task.id ? '0 4px 12px rgba(33, 150, 243, 0.3)' : 'none',
            '&:hover': { opacity: editingTask === task.id ? 1 : 0.8 },
            position: 'relative',
          }}
          onClick={() => handleTaskClick(task)}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 'bold', fontSize: '11px', lineHeight: 1.2 }}
          >
            {task.name} ({formatDecimalHours(task.estimatedHours || 4)})
          </Typography>
          {showDueDate && originalOrder?.planned_production_date && (
            <Typography variant="caption" sx={{ fontSize: '10px', opacity: 0.8, mt: 0.5 }}>
              Due: {new Date(originalOrder.planned_production_date).toLocaleDateString()}
            </Typography>
          )}
          {task.production_line_name && (
            <Chip
              label={task.production_line_name}
              size="small"
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                height: 16,
                fontSize: '8px',
                bgcolor: getLineColor(task.production_line_id || 1),
                color: 'white',
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
        </Box>
      );
    }
  );

  // Process all data once when component loads/data changes
  const processedData = useMemo(() => {
    // Return empty data if overallTimeRange is null
    if (!overallTimeRange) {
      return {
        breakOverlaps: new Map<string, any>(),
        taskConflicts: new Map<string, any>(),
        taskBreakSpanning: new Map<string, any>(),
      };
    }

    const breakOverlaps = new Map<string, any>();
    const taskConflicts = new Map<string, any>();
    const taskBreakSpanning = new Map<string, any>();

    // Process break overlaps for each date
    dateRange.forEach((date) => {
      const dateKey = date.toISOString().split('T')[0];
      const dateWorkingHours = getWorkingHoursForDate(date);

      if (dateWorkingHours) {
        const breakOverlap = calculateBreakTimeOverlap(
          dateWorkingHours,
          overallTimeRange,
          HOUR_HEIGHT
        );
        breakOverlaps.set(dateKey, breakOverlap);
      }
    });

    // Process task-specific data (conflicts, break-spanning, etc.)
    visibleTasks.forEach((task) => {
      // Store conflict analysis data
      if (task.conflictAnalysis) {
        taskConflicts.set(task.id, {
          hasConflicts: task.conflictAnalysis.hasConflicts,
          severity: task.conflictAnalysis.highestSeverity,
          count: task.conflictAnalysis.conflictCount,
          conflicts: task.conflictAnalysis.conflicts,
        });
      }

      // Store spanning data (break or day-end)
      if (
        task.is_split_order &&
        (task.first_portion_minutes || task.pre_break_minutes) &&
        (task.second_portion_minutes || task.post_break_minutes)
      ) {
        const spanningType = task.spanning_type || 'break';
        taskBreakSpanning.set(task.id, {
          isBreakSpanning: spanningType === 'break',
          isDaySpanning: spanningType === 'day_end',
          preBreakDuration: task.first_portion_minutes ?? task.pre_break_minutes ?? 0,
          postBreakDuration: task.second_portion_minutes ?? task.post_break_minutes ?? 0,
          breakPauseStart: task.break_pause_start,
          breakPauseEnd: task.second_portion_start_time || task.break_pause_end,
          secondPortionDate: task.second_portion_date,
        });
      }
    });

    return {
      breakOverlaps,
      taskConflicts,
      taskBreakSpanning,
    };
  }, [dateRange, workingHoursConfig, overallTimeRange, HOUR_HEIGHT, visibleTasks]);

  // Render error UI if no dates with available hours
  if (hasError) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        <Typography variant="h6" gutterBottom>
          No Available Hours
        </Typography>
        <Typography variant="body2">{errorMessage}</Typography>
        <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
          Please configure working hours for the selected date range.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        height: 'auto',
        position: 'relative',
        alignItems: 'flex-start',
      }}
    >
      {/* Main Multi-Line Gantt Chart */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          position: 'relative',
        }}
      >
        {/* Controls */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}></Box>

        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            overflow: shouldScroll ? 'auto' : 'visible',
            height: 'fit-content',
            position: 'relative',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#f5f5f5',
              minWidth:
                shouldScroll && typeof dayWidth === 'number'
                  ? `${TIME_COLUMN_WIDTH + dateRange.length * dayWidth * visibleLines.length}px`
                  : 'auto',
              width: actualViewMode === 'Day' ? '100%' : 'auto',
            }}
          >
            <Box
              sx={{
                width: TIME_COLUMN_WIDTH,
                p: 1,
                textAlign: 'center',
                borderRight: '1px solid #e0e0e0',
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                Time
              </Typography>
            </Box>

            {dateRange.map((date, dateIndex) => {
              const _isOverCapacity = visibleLines.some((line) => {
                const capacity = calculateLineCapacity(line.id, date);
                return capacity.utilization > 100;
              });

              const hasZeroHours = visibleLines.some((line) => {
                const capacity = calculateLineCapacity(line.id, date);
                return capacity.availableHours === 0;
              });

              let bgColor = 'transparent';
              if (isToday(date)) {
                bgColor = '#fff3cd';
              } else if (hasZeroHours) {
                bgColor = '#f5f5f5';
              }

              return (
                <Box
                  key={dateIndex}
                  sx={{
                    width:
                      actualViewMode === 'Day' && dayWidth === 'auto'
                        ? '100%'
                        : typeof dayWidth === 'number'
                          ? dayWidth * visibleLines.length
                          : undefined,
                    flex: actualViewMode === 'Day' && dayWidth === 'auto' ? 1 : undefined,
                    borderRight: dateIndex < dateRange.length - 1 ? '1px solid #e0e0e0' : 'none',
                    bgcolor: bgColor,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Date Header */}
                  <Box sx={{ p: 1, textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" fontWeight={isToday(date) ? 'bold' : 'normal'}>
                      {date.toLocaleDateString('en', { weekday: 'short' })} {date.getDate()}{' '}
                      {date.toLocaleDateString('en', { month: 'short' })}
                    </Typography>
                  </Box>

                  {/* Production Line Headers */}
                  <Box sx={{ display: 'flex' }}>
                    {visibleLines.map((line, lineIndex) => {
                      const capacity = calculateLineCapacity(line.id, date);
                      const isOverCapacity = capacity.utilization > 100;
                      const hasZeroHours = capacity.availableHours === 0;
                      const hasScheduledWork = capacity.totalHours > 0;
                      const lineColor = getLineColor(line.id);

                      return (
                        <Box
                          key={line.id}
                          sx={{
                            width:
                              actualViewMode === 'Day' && dayWidth === 'auto'
                                ? undefined
                                : dayWidth,
                            flex: actualViewMode === 'Day' && dayWidth === 'auto' ? 1 : undefined,
                            p: 0.25,
                            textAlign: 'center',
                            borderRight:
                              lineIndex < visibleLines.length - 1 ? '1px solid #e0e0e0' : 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            bgcolor: `${lineColor}10`,
                            minHeight: 20,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: lineColor,
                                fontWeight: 'bold',
                                fontSize: '10px',
                                lineHeight: 1,
                              }}
                            >
                              {line.name}
                            </Typography>
                            {line.lineType && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '8px',
                                  lineHeight: 1,
                                  px: 0.5,
                                  py: 0.1,
                                  borderRadius: 0.5,
                                  bgcolor: line.lineType === 'machine' ? '#e3f2fd' : '#fff3e0',
                                  color: line.lineType === 'machine' ? '#1565c0' : '#e65100',
                                  fontWeight: 'bold',
                                }}
                              >
                                {line.lineType === 'machine' ? 'M' : 'H'}
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                isOverCapacity || (hasZeroHours && hasScheduledWork)
                                  ? 'error.main'
                                  : 'text.secondary',
                              fontWeight:
                                isOverCapacity || (hasZeroHours && hasScheduledWork)
                                  ? 'bold'
                                  : 'normal',
                              fontSize: '9px',
                              lineHeight: 1,
                            }}
                          >
                            {capacity.totalHours.toFixed(1)}h/{capacity.availableHours}h
                          </Typography>
                          <Box
                            sx={{
                              width: '100%',
                              height: 2,
                              bgcolor: '#e0e0e0',
                              borderRadius: 1,
                              mt: 0.25,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${Math.min(capacity.utilization, 100)}%`,
                                height: '100%',
                                bgcolor:
                                  isOverCapacity || (hasZeroHours && hasScheduledWork)
                                    ? 'error.main'
                                    : capacity.utilization > 80
                                      ? 'warning.main'
                                      : lineColor,
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Horizontal Multi-Line Schedule */}
          <Box
            sx={{
              display: 'flex',
              position: 'relative',
              height: overallTimeRange.totalHours * HOUR_HEIGHT,
              minWidth:
                shouldScroll && typeof dayWidth === 'number'
                  ? `${TIME_COLUMN_WIDTH + dateRange.length * dayWidth * visibleLines.length}px`
                  : 'auto',
            }}
          >
            {/* Time labels column */}
            <Box
              sx={{
                width: TIME_COLUMN_WIDTH,
                borderRight: '1px solid #e0e0e0',
                bgcolor: '#f9f9f9',
              }}
            >
              {hours.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    height: HOUR_HEIGHT,
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    px: 0.5,
                    pt: 0.5,
                  }}
                >
                  <Typography variant="body2" fontSize="12px" color="text.secondary">
                    {formatHour(hour)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Date columns with production lines side by side */}
            {dateRange.map((date, dateIndex) => {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const dateKey = `${year}-${month}-${day}`;

              // Check if this date is an active workday
              const isActiveDay = isActiveCache?.get(dateKey) ?? true; // Default to true if not in cache

              return (
                <Box
                  key={dateIndex}
                  sx={{
                    width:
                      typeof dayWidth === 'number' ? dayWidth * visibleLines.length : undefined,
                    flex: actualViewMode === 'Day' && dayWidth === 'auto' ? 1 : undefined,
                    borderRight: dateIndex < dateRange.length - 1 ? '1px solid #e0e0e0' : 'none',
                    position: 'relative',
                    display: 'flex',
                    bgcolor: isToday(date) ? '#fff3cd' : 'transparent',
                  }}
                >
                  {/* Non-active day overlay (weekends, holidays, etc.) */}
                  {!isActiveDay && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(128, 128, 128, 0.4)', // Darker grey for entire day
                        zIndex: 2, // Above non-working hours but below tasks
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '10px',
                          color: '#666',
                          fontWeight: 'bold',
                          transform: 'rotate(-45deg)',
                        }}
                      >
                        Non-Working Day
                      </Typography>
                    </Box>
                  )}
                  {/* Production lines side by side for this date */}
                  {visibleLines.map((line, lineIndex) => {
                    const lineColor = getLineColor(line.id);
                    const lineTasksByDate = tasksByDateAndLine.get(line.id) || new Map();
                    const dayTasks = lineTasksByDate.get(dateKey) || [];

                    return (
                      <Box
                        key={line.id}
                        sx={{
                          width:
                            actualViewMode === 'Day' && dayWidth === 'auto' ? undefined : dayWidth,
                          flex: actualViewMode === 'Day' && dayWidth === 'auto' ? 1 : undefined,
                          borderRight:
                            lineIndex < visibleLines.length - 1 ? '1px solid #e0e0e0' : 'none',
                          position: 'relative',
                          bgcolor: `${lineColor}05`,
                        }}
                      >
                        {/* Hour grid lines */}
                        {hours.map((hour) => (
                          <Box
                            key={hour}
                            sx={{
                              position: 'absolute',
                              top: (hour - overallTimeRange.startHour) * HOUR_HEIGHT,
                              left: 0,
                              right: 0,
                              height: HOUR_HEIGHT,
                              borderBottom: '1px solid #e0e0e0',
                            }}
                          />
                        ))}

                        {/* Non-working hours overlay (grey out hours outside working time) */}
                        {(() => {
                          const dateWorkingHours = getWorkingHoursForDate(date);
                          if (!dateWorkingHours) return null;

                          const dayStartHour = timeStringToDecimalHours(
                            extractTimeString(dateWorkingHours.startTime)
                          );
                          const dayEndHour = timeStringToDecimalHours(
                            extractTimeString(dateWorkingHours.endTime)
                          );

                          const overlays = [];

                          // Grey out hours before work starts
                          if (dayStartHour > overallTimeRange.startHour) {
                            const beforeWorkTop = 0;
                            const beforeWorkHeight =
                              (dayStartHour - overallTimeRange.startHour) * HOUR_HEIGHT;

                            overlays.push(
                              <Box
                                key="before-work"
                                sx={{
                                  position: 'absolute',
                                  top: beforeWorkTop,
                                  left: 0,
                                  right: 0,
                                  height: beforeWorkHeight,
                                  bgcolor: 'rgba(128, 128, 128, 0.3)', // Grey overlay
                                  zIndex: 0, // Behind tasks
                                  pointerEvents: 'none',
                                }}
                              />
                            );
                          }

                          // Grey out hours after work ends
                          if (dayEndHour < overallTimeRange.endHour) {
                            const afterWorkTop =
                              (dayEndHour - overallTimeRange.startHour) * HOUR_HEIGHT;
                            const afterWorkHeight =
                              (overallTimeRange.endHour - dayEndHour) * HOUR_HEIGHT;

                            overlays.push(
                              <Box
                                key="after-work"
                                sx={{
                                  position: 'absolute',
                                  top: afterWorkTop,
                                  left: 0,
                                  right: 0,
                                  height: afterWorkHeight,
                                  bgcolor: 'rgba(128, 128, 128, 0.3)', // Grey overlay
                                  zIndex: 0, // Behind tasks
                                  pointerEvents: 'none',
                                }}
                              />
                            );
                          }

                          return overlays;
                        })()}

                        {/* Break time visualization */}
                        {(() => {
                          const breakOverlap = processedData.breakOverlaps.get(dateKey);

                          if (!breakOverlap?.hasOverlap) {
                            return null;
                          }

                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: breakOverlap.breakTop,
                                left: 0,
                                right: 0,
                                height: breakOverlap.breakHeight,
                                bgcolor: 'rgba(255, 193, 7, 0.2)', // Light amber for break
                                border: '1px dashed #ffc107',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1, // Above non-working hours overlay but behind tasks
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '8px',
                                  color: '#f57c00',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  transform: 'rotate(-45deg)',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                BREAK
                              </Typography>
                            </Box>
                          );
                        })()}

                        {/* Task blocks for this production line */}
                        {dayTasks.map((task: GanttTask, taskIndex: number) => {
                          try {
                            // Validate and fix task duration
                            let taskDuration = task.estimatedHours || 4;
                            if (taskDuration <= 0 || !isFinite(taskDuration)) {
                              console.warn(
                                `[MultiLineGantt] Invalid task duration for task ${task.id}: ${taskDuration}. Using fallback.`
                              );
                              taskDuration = 0.5; // Default to 30 minutes
                            }

                            // Validate task properties
                            if (!task.id || !task.name) {
                              console.warn(`Task missing required properties:`, task);
                              return null;
                            }

                            const conflictAnalysis = task.conflictAnalysis;
                            const hasConflicts = conflictAnalysis?.hasConflicts || false;
                            const conflictSeverity = conflictAnalysis?.highestSeverity || 'none';

                            // Get working hours for this specific date
                            const dateWorkingHours = getWorkingHoursForDate(date);
                            if (!dateWorkingHours) {
                              console.warn(`No working hours found for date ${dateKey}`);
                              return null;
                            }

                            const dayStartHour = timeStringToDecimalHours(
                              extractTimeString(dateWorkingHours.startTime)
                            );
                            const dayEndHour = timeStringToDecimalHours(
                              extractTimeString(dateWorkingHours.endTime)
                            );

                            let startHour = Math.floor(dayStartHour);
                            let startMinute = Math.round(
                              (dayStartHour - Math.floor(dayStartHour)) * 60
                            );

                            // Use task.startTime - it should always be available since we validate it during task creation
                            const timeSource = task.startTime;

                            if (timeSource) {
                              // Handle both time-only strings (07:00:00) and datetime strings (1970-01-01T07:00:00.000Z)
                              let timeStr = timeSource;
                              if (timeStr.includes('T')) {
                                // Extract time part from datetime string
                                timeStr = timeStr.split('T')[1].split('.')[0]; // Remove timezone and milliseconds
                              }

                              const timeParts = timeStr.split(':');
                              const hour = parseInt(timeParts[0]);
                              const minute = parseInt(timeParts[1]) || 0;
                              // Round minute to nearest 5 minutes
                              const roundedMinute = Math.round(minute / 5) * 5;
                              // Adjust hour if rounding minute to 60
                              let adjustedHour = hour;
                              let adjustedMinute = roundedMinute;
                              if (roundedMinute === 60) {
                                adjustedHour = hour + 1;
                                adjustedMinute = 0;
                              }
                              // Validate the time is within working hours for this date
                              const plannedTime = adjustedHour + adjustedMinute / 60;
                              if (plannedTime >= dayStartHour && plannedTime <= dayEndHour) {
                                startHour = adjustedHour;
                                startMinute = adjustedMinute;
                              }
                            } else {
                              console.error(
                                `[MultiLineGantt] Task ${task.id} missing startTime - this should not happen with proper validation`
                              );
                            }

                            let taskBgColor = getTaskColor(task.custom_class);
                            let borderColor = lineColor;

                            if (hasConflicts) {
                              borderColor =
                                conflictSeverity === 'critical'
                                  ? '#d32f2f'
                                  : conflictSeverity === 'error'
                                    ? '#ff9800'
                                    : conflictSeverity === 'warning'
                                      ? '#ffeb3b'
                                      : '#f44336';
                              if (conflictSeverity === 'critical') {
                                taskBgColor = '#d32f2f';
                              } else if (conflictSeverity === 'error') {
                                taskBgColor = '#f57c00';
                              }
                            }

                            // Check if this is a break-spanning order
                            const isBreakSpanning = false; // Disabled - tasks are already split in ProductionSchedule.tsx

                            if (isBreakSpanning) {
                              // Render break-spanning task as two segments
                              const preBreakDuration = task.pre_break_minutes!;
                              const postBreakDuration = task.post_break_minutes!;
                              const _breakStartTime = timeStringToDecimalHours(
                                task.break_pause_start!
                              );
                              const breakEndTime = timeStringToDecimalHours(task.break_pause_end!);

                              const startTimeInMinutes = startHour * 60 + startMinute;
                              const visualStartHour = startTimeInMinutes / 60;

                              // Pre-break segment
                              const preBreakTop =
                                (visualStartHour - overallTimeRange.startHour) * HOUR_HEIGHT;
                              const preBreakHeight = Math.max(
                                20,
                                (preBreakDuration / 60) * HOUR_HEIGHT
                              );

                              // Post-break segment (starts after break ends)
                              const postBreakTop =
                                (breakEndTime - overallTimeRange.startHour) * HOUR_HEIGHT;
                              const postBreakHeight = Math.max(
                                20,
                                (postBreakDuration / 60) * HOUR_HEIGHT
                              );

                              return (
                                <React.Fragment key={`${task.id}-${taskIndex}`}>
                                  {/* Pre-break segment */}
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: preBreakTop + 2,
                                      left: 4,
                                      right: 4,
                                      height: preBreakHeight - 4,
                                      bgcolor: taskBgColor,
                                      color: 'white',
                                      borderRadius: '4px 4px 0 0', // Rounded top only
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      textAlign: 'center',
                                      overflow: 'visible',
                                      zIndex: editingTask === task.id ? 10 : 2,
                                      border: editingTask === task.id ? '2px solid #2196f3' : `0px`,
                                      borderBottom: '1px dashed rgba(255, 255, 255, 0.5)', // Dashed bottom to indicate continuation
                                      boxShadow:
                                        editingTask === task.id
                                          ? '0 4px 12px rgba(33, 150, 243, 0.3)'
                                          : hasConflicts
                                            ? `0 2px 8px ${borderColor}40`
                                            : 'none',
                                      '&:hover': { opacity: editingTask === task.id ? 1 : 0.8 },
                                    }}
                                    onClick={() => handleTaskClick(task)}
                                  >
                                    {/* Break-spanning indicator */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: -2,
                                        left: -2,
                                        width: 16,
                                        height: 16,
                                        bgcolor: '#ffc107',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '8px',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        zIndex: 3,
                                        border: '1px solid white',
                                      }}
                                    >
                                      ⏸
                                    </Box>

                                    {/* Conflict indicator */}
                                    {hasConflicts && (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: -2,
                                          right: -2,
                                          width: 16,
                                          height: 16,
                                          bgcolor:
                                            conflictSeverity === 'critical'
                                              ? '#d32f2f'
                                              : conflictSeverity === 'error'
                                                ? '#ff9800'
                                                : conflictSeverity === 'warning'
                                                  ? '#ffeb3b'
                                                  : '#f44336',
                                          borderRadius: '50%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '8px',
                                          fontWeight: 'bold',
                                          color: 'white',
                                          zIndex: 3,
                                          border: '1px solid white',
                                        }}
                                      >
                                        {conflictAnalysis?.conflictCount || 0}
                                      </Box>
                                    )}

                                    {/* Progress bar for pre-break segment */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: `${Math.min(task.progress, (preBreakDuration / 60 / taskDuration) * 100)}%`,
                                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                                        borderRadius: '4px 0 0 0',
                                      }}
                                    />

                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        zIndex: 1,
                                        fontSize: '9px',
                                        lineHeight: 1.1,
                                        whiteSpace: 'normal',
                                        overflow: 'hidden',
                                        textAlign: 'center',
                                        wordBreak: 'break-word',
                                        px: 1,
                                      }}
                                    >
                                      {task.name} (1/2)
                                    </Typography>
                                  </Box>

                                  {/* Post-break segment */}
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: postBreakTop + 2,
                                      left: 4,
                                      right: 4,
                                      height: postBreakHeight - 4,
                                      bgcolor: taskBgColor,
                                      color: 'white',
                                      borderRadius: '0 0 4px 4px', // Rounded bottom only
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      textAlign: 'center',
                                      overflow: 'visible',
                                      zIndex: editingTask === task.id ? 10 : 2,
                                      border: editingTask === task.id ? '2px solid #2196f3' : `0px`,
                                      borderTop: '1px dashed rgba(255, 255, 255, 0.5)', // Dashed top to indicate continuation
                                      boxShadow:
                                        editingTask === task.id
                                          ? '0 4px 12px rgba(33, 150, 243, 0.3)'
                                          : hasConflicts
                                            ? `0 2px 8px ${borderColor}40`
                                            : 'none',
                                      '&:hover': { opacity: editingTask === task.id ? 1 : 0.8 },
                                    }}
                                    onClick={() => handleTaskClick(task)}
                                  >
                                    {/* Progress bar for post-break segment */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: `${Math.max(0, Math.min(100, ((task.progress - (preBreakDuration / 60 / taskDuration) * 100) / (postBreakDuration / 60 / taskDuration)) * 100))}%`,
                                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                                        borderRadius: '0 0 4px 0',
                                      }}
                                    />

                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        zIndex: 1,
                                        fontSize: '9px',
                                        lineHeight: 1.1,
                                        whiteSpace: 'normal',
                                        overflow: 'hidden',
                                        textAlign: 'center',
                                        wordBreak: 'break-word',
                                        px: 1,
                                      }}
                                    >
                                      {task.name} (2/2)
                                    </Typography>
                                  </Box>

                                  {/* Connection line between segments */}
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: preBreakTop + preBreakHeight,
                                      left: typeof dayWidth === 'number' ? dayWidth / 2 - 1 : '50%',
                                      width: 2,
                                      height: postBreakTop - (preBreakTop + preBreakHeight),
                                      bgcolor: 'rgba(255, 193, 7, 0.6)',
                                      zIndex: 1,
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: 8,
                                        height: 8,
                                        bgcolor: '#ffc107',
                                        borderRadius: '50%',
                                        border: '1px solid white',
                                      },
                                    }}
                                  />
                                </React.Fragment>
                              );
                            } else {
                              // Render regular (non-break-spanning) task
                              const startTimeInMinutes = startHour * 60 + startMinute;
                              const taskDurationInMinutes = Math.round(taskDuration * 60);

                              const visualStartHour = startTimeInMinutes / 60;
                              const visualDurationHours = taskDurationInMinutes / 60;

                              let top =
                                (visualStartHour - overallTimeRange.startHour) * HOUR_HEIGHT;
                              const calculatedHeight = visualDurationHours * HOUR_HEIGHT;
                              // Enforce minimum height of 20px for very short orders (Bug 58)
                              const height = Math.max(20, calculatedHeight);

                              // Fix for break-spanning P2 tasks: if this is a P2 task that should start right after break,
                              // adjust positioning to eliminate gap
                              if (task.part === 'p2' && task.spanning_type === 'break') {
                                const breakOverlap = processedData.breakOverlaps.get(dateKey);
                                if (breakOverlap?.hasOverlap) {
                                  // Break block ends at: breakTop + breakHeight
                                  const breakEndPx =
                                    breakOverlap.breakTop + breakOverlap.breakHeight;
                                  // If task is positioned close to but after the break end, adjust to start right after break
                                  const gap = top - breakEndPx;
                                  if (gap > 0 && gap < 20) {
                                    // Gap is small (less than 20px, likely 5 minutes)
                                    top = breakEndPx;
                                  }
                                }
                              }

                              // Clamp to workday end time to prevent overflow (Bug 61)
                              const dayEndTimeInMinutes = Math.round(dayEndHour * 60);
                              const taskEndTimeInMinutes =
                                startTimeInMinutes + taskDurationInMinutes;
                              const clampedEndTime = Math.min(
                                taskEndTimeInMinutes,
                                dayEndTimeInMinutes
                              );
                              const clampedDuration = clampedEndTime - startTimeInMinutes;
                              const clampedHeight = Math.max(
                                20,
                                (clampedDuration / 60) * HOUR_HEIGHT
                              );

                              return (
                                <Box
                                  key={`${task.id}-${task.part || 'single'}-${taskIndex}`}
                                  sx={{
                                    position: 'absolute',
                                    top: top + 2,
                                    left: 4,
                                    right: 4,
                                    height: Math.min(clampedHeight - 4, height - 4),
                                    bgcolor: taskBgColor,
                                    color: 'white',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    overflow: 'visible',
                                    zIndex: editingTask === task.id ? 10 : 1,
                                    border: editingTask === task.id ? '2px solid #2196f3' : `0px`,
                                    boxShadow:
                                      editingTask === task.id
                                        ? '0 4px 12px rgba(33, 150, 243, 0.3)'
                                        : hasConflicts
                                          ? `0 2px 8px ${borderColor}40`
                                          : 'none',
                                    '&:hover': { opacity: editingTask === task.id ? 1 : 0.8 },
                                  }}
                                  onClick={() => handleTaskClick(task)}
                                >
                                  {/* Conflict indicator */}
                                  {hasConflicts && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: -2,
                                        right: -2,
                                        width: 16,
                                        height: 16,
                                        bgcolor:
                                          conflictSeverity === 'critical'
                                            ? '#d32f2f'
                                            : conflictSeverity === 'error'
                                              ? '#ff9800'
                                              : conflictSeverity === 'warning'
                                                ? '#ffeb3b'
                                                : '#f44336',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '8px',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        zIndex: 2,
                                        border: '1px solid white',
                                      }}
                                    >
                                      {conflictAnalysis?.conflictCount || 0}
                                    </Box>
                                  )}

                                  {/* Progress bar */}
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: `${task.progress}%`,
                                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                                      borderRadius: '4px 0 0 4px',
                                    }}
                                  />

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 'bold',
                                      position: 'relative',
                                      zIndex: 1,
                                      fontSize: '10px',
                                      lineHeight: 1.2,
                                      whiteSpace: 'normal',
                                      overflow: 'hidden',
                                      textAlign: 'center',
                                      wordBreak: 'break-word',
                                      px: 1,
                                    }}
                                  >
                                    {task.name}
                                  </Typography>
                                </Box>
                              );
                            }
                          } catch (taskError) {
                            console.error(`Error rendering task ${task.id}:`, taskError);
                            return (
                              <Box
                                key={`${task.id}-${taskIndex}-error`}
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 4,
                                  right: 4,
                                  height: 30,
                                  bgcolor: '#ffebee',
                                  color: '#d32f2f',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '10px',
                                  border: '1px solid #d32f2f',
                                }}
                              >
                                Error rendering task
                              </Box>
                            );
                          }
                        })}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          {/* Global Mini-popup for editing task */}
          {editingTask && (
            <Box
              data-mini-popup
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 1,
                width: 'auto',
                minWidth: 200,
                maxWidth: 250,
                bgcolor: '#fff',
                borderRadius: 1,
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 99999,
                border: '2px solid #2196f3',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                // Find the task across all lines and dates
                let foundTask: GanttTask | null = null;
                let originalOrder: any = null;

                for (const line of visibleLines) {
                  const lineTasksByDate = tasksByDateAndLine.get(line.id) || new Map();
                  for (const [, dayTasks] of lineTasksByDate) {
                    // Find task by matching composite key (id-part) or just id
                    const task = dayTasks.find((t: GanttTask) => {
                      const taskKey = t.part ? `${t.id}-${t.part}` : t.id;
                      return taskKey === editingTask;
                    });
                    if (task) {
                      foundTask = task;
                      // Task ID is now the original order ID
                      originalOrder = productionOrders?.find(
                        (order) => String(order.production_order_id) === task.id
                      );
                      break;
                    }
                  }
                  if (foundTask) break;
                }

                if (!foundTask) return null;

                const conflictAnalysis = foundTask.conflictAnalysis;
                const hasConflicts = conflictAnalysis?.hasConflicts || false;

                return (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 'bold',
                          color: '#333',
                          fontSize: '11px',
                          lineHeight: 1.2,
                        }}
                      >
                        {foundTask.name}
                      </Typography>
                      <button
                        onClick={handleCloseMiniPopup}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '16px',
                          cursor: 'pointer',
                          color: '#666',
                          padding: '0',
                          marginLeft: '8px',
                        }}
                      >
                        ×
                      </button>
                    </Box>

                    <Box sx={{ fontSize: '10px', color: '#666', lineHeight: 1.3 }}>
                      <div>Duration: {formatDecimalHours(foundTask.estimatedHours || 4)}</div>
                      <div>Progress: {foundTask.progress}%</div>
                      <div>Line: {foundTask.production_line_name || 'Unknown'}</div>
                      {originalOrder?.due_date && (
                        <div>Due: {new Date(originalOrder.due_date).toLocaleDateString()}</div>
                      )}
                      {originalOrder?.product_code && <div>Sku: {originalOrder.product_code}</div>}
                      {originalOrder?.planned_production_date && (
                        <div>
                          Schedule:{' '}
                          {(() => {
                            // Use UTC date to avoid timezone issues
                            const date = new Date(originalOrder.planned_production_date);
                            return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
                          })()}
                          {(() => {
                            const formatTimeDisplay = (timeStr: string | undefined) => {
                              if (!timeStr) return '';
                              if (timeStr.includes('T')) {
                                const date = new Date(timeStr);
                                const hours = date.getUTCHours();
                                const minutes = date.getUTCMinutes();
                                const period = hours >= 12 ? 'PM' : 'AM';
                                const displayHours =
                                  hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                                return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
                              }
                              // Handle time format (e.g., "13:00:00")
                              const [hours, mins] = timeStr.split(':').map(Number);
                              const period = hours >= 12 ? 'PM' : 'AM';
                              const displayHours =
                                hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                              return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
                            };

                            // For split orders, show the time for the specific part being viewed
                            if (foundTask.part && originalOrder?.is_split_order) {
                              const schedulePortions = (originalOrder as any).schedule_portions as
                                | Array<{
                                    portion_number: number;
                                    scheduled_date: string;
                                    scheduled_time: string;
                                    duration_minutes: number;
                                  }>
                                | undefined;

                              if (schedulePortions) {
                                // Extract part number from foundTask.part (e.g., 'p2' -> 2)
                                const partNumber = parseInt(foundTask.part.replace('p', '')) - 1;
                                const portion = schedulePortions[partNumber];
                                if (portion) {
                                  return <span> {formatTimeDisplay(portion.scheduled_time)}</span>;
                                }
                              }

                              // Fallback to legacy fields for backward compatibility
                              if (foundTask.part === 'p1') {
                                // Show first part (pre-break) start time
                                return originalOrder.planned_production_time ? (
                                  <span>
                                    {' '}
                                    {formatTimeDisplay(originalOrder.planned_production_time)}
                                  </span>
                                ) : (
                                  ''
                                );
                              } else if (foundTask.part === 'p2') {
                                // Show second part (second portion) start time
                                const secondPortionTime =
                                  originalOrder.second_portion_start_time ||
                                  originalOrder.break_pause_end;
                                return secondPortionTime ? (
                                  <span> {formatTimeDisplay(secondPortionTime)}</span>
                                ) : (
                                  ''
                                );
                              }
                            }

                            // Regular order or show both times for spanning orders
                            if (
                              originalOrder?.is_split_order &&
                              (originalOrder?.first_portion_minutes ||
                                originalOrder?.pre_break_minutes) &&
                              (originalOrder?.second_portion_minutes ||
                                originalOrder?.post_break_minutes)
                            ) {
                              // Show both times
                              const firstPortionTime = originalOrder.planned_production_time;
                              const secondPortionTime =
                                originalOrder.second_portion_start_time ||
                                originalOrder.break_pause_end;
                              return (
                                <span>
                                  {' '}
                                  {formatTimeDisplay(firstPortionTime)} -{' '}
                                  {formatTimeDisplay(secondPortionTime)}
                                </span>
                              );
                            }

                            // Regular order - show single time
                            if (originalOrder?.planned_production_time) {
                              return (
                                <span>
                                  {' '}
                                  {formatTimeDisplay(originalOrder.planned_production_time)}
                                </span>
                              );
                            }
                            return '';
                          })()}
                        </div>
                      )}
                    </Box>

                    {/* Conflict information */}
                    {hasConflicts && conflictAnalysis && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1,
                          bgcolor: '#fff3cd',
                          borderRadius: 1,
                          border: '1px solid #ffc107',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '10px', fontWeight: 'bold', color: '#856404' }}
                        >
                          ⚠️ {conflictAnalysis.conflictCount} Conflict
                          {conflictAnalysis.conflictCount > 1 ? 's' : ''} Detected
                        </Typography>
                        <Box sx={{ mt: 0.5, maxHeight: 60, overflow: 'auto' }}>
                          {conflictAnalysis.conflicts
                            .slice(0, 3)
                            .map((conflict: { message: string }, index: number) => (
                              <Typography
                                key={index}
                                variant="caption"
                                sx={{
                                  fontSize: '9px',
                                  color: '#856404',
                                  display: 'block',
                                  mb: 0.25,
                                }}
                              >
                                • {conflict.message}
                              </Typography>
                            ))}
                          {conflictAnalysis.conflicts.length > 3 && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '9px',
                                color: '#856404',
                                fontStyle: 'italic',
                              }}
                            >
                              +{conflictAnalysis.conflicts.length - 3} more...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: '10px', fontWeight: 'bold', color: '#333' }}
                      >
                        Schedule:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <input
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '3px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            fontSize: '10px',
                          }}
                        />
                        <input
                          type="time"
                          value={editTime}
                          onChange={(e) => setEditTime(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '3px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            fontSize: '10px',
                          }}
                        />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        justifyContent: 'flex-end',
                        mt: 0.5,
                      }}
                    >
                      <button
                        onClick={handleSaveEdit}
                        style={{
                          padding: '3px 6px',
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          fontSize: '10px',
                          cursor: 'pointer',
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => handleEditOrder(foundTask.id)}
                        style={{
                          padding: '3px 6px',
                          backgroundColor: '#2196f3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          fontSize: '10px',
                          cursor: 'pointer',
                        }}
                      >
                        Edit Order
                      </button>
                      <button
                        onClick={() => handleUnschedule(foundTask.id)}
                        style={{
                          padding: '3px 6px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          fontSize: '10px',
                          cursor: 'pointer',
                        }}
                      >
                        Unschedule
                      </button>
                    </Box>
                  </>
                );
              })()}
            </Box>
          )}

          {/* Empty State */}
          {visibleLines.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: '#666' }}>
              <Typography>No production lines available</Typography>
            </Box>
          )}

          {visibleLines.length > 0 && visibleTasks.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: '#666' }}>
              <Typography>No production orders scheduled for this period</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Sidebar */}
      {shouldShowSidebar && (
        <Paper
          sx={{
            width: SIDEBAR_WIDTH,
            display: 'flex',
            flexDirection: 'column',
            height: 'fit-content',
            maxHeight: '70vh',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <Tabs
            value={sidebarTab}
            onChange={(_, newValue) => setSidebarTab(newValue)}
            sx={{ borderBottom: '1px solid #e0e0e0' }}
          >
            <Tab
              label={
                <Badge badgeContent={unscheduledTasks.length} color="primary">
                  Unscheduled
                </Badge>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Past Due</Typography>
                  <Badge badgeContent={pastDueTasks.length} color="error" />
                </Box>
              }
            />
          </Tabs>

          <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
            {sidebarTab === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Click on orders to edit their schedule
                </Typography>
                {unscheduledTasks.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {unscheduledTasks.map((task) => (
                      <TaskChip key={`${task.id}-${task.part || 'single'}`} task={task} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No unscheduled orders
                  </Typography>
                )}
              </Box>
            )}

            {sidebarTab === 1 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Orders scheduled for past dates that are not completed
                </Typography>
                {pastDueTasks.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {pastDueTasks.map((task) => (
                      <TaskChip key={task.id} task={task} showDueDate={true} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No past due orders
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default MultiLineGantt;
